import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../dashboard/Dashboard.css';
import './ShipmentsPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import IconButton from '../dashboard/components/product_item_components/IconButton';
import { TrashIcon } from '../dashboard/components/product_item_components/icons';
import DeleteProductModal from '../dashboard/components/product_item_components/DeleteProductModal';

function formatPrice(price) {
  const n = typeof price === 'string' ? parseFloat(price) : Number(price);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function ShipmentsPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { loading: httpLoading, requestWithMeta } = useHttp();

  const [shipments, setShipments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [shipmentToDelete, setShipmentToDelete] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState(null);

  const fetchShipments = React.useCallback(async () => {
    if (!branchId || branchId === 'store') {
      setShipments([]);
      setLoading(false);
      setError('Выберите филиал');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await requestWithMeta(
        `/operator/branches/${encodeURIComponent(branchId)}/shipments`,
        'GET'
      );
      if (!res?.ok) {
        setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить доставки');
        setShipments([]);
        return;
      }
      const list = Array.isArray(res?.data?.shipments) ? res.data.shipments : [];
      setShipments(list);
    } catch (_) {
      setError('Ошибка загрузки');
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, requestWithMeta]);

  React.useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  React.useEffect(() => {
    if (!shipmentToDelete) setDeleteError(null);
  }, [shipmentToDelete]);

  const handleBack = () => {
    navigate(`/branch/${branchId || 'store'}/shipments`);
  };

  const handleCreate = () => {
    navigate(`/branch/${branchId}/shipments/create`);
  };

  const handleEdit = (shipment) => {
    const id = shipment?.id ?? shipment?._id;
    if (id == null) return;
    navigate(`/branch/${branchId}/shipments/${encodeURIComponent(id)}/edit`, {
      state: { shipment },
    });
  };

  const handleConfirmDelete = async () => {
    const shipment = shipmentToDelete;
    const id = shipment?.id ?? shipment?._id;
    if (id == null) return;

    setDeleteError(null);
    const res = await requestWithMeta(
      `/operator/shipments/${encodeURIComponent(id)}`,
      'DELETE'
    );
    if (!res?.ok) {
      setDeleteError(res?.data?.error || res?.data?.message || 'Не удалось удалить доставку');
      return;
    }

    setShipments((prev) =>
      prev.filter((s) => String(s?.id ?? s?._id) !== String(id))
    );
    setShipmentToDelete(null);
  };

  const deleteName =
    shipmentToDelete?.locationName ??
    (shipmentToDelete ? `Доставка #${shipmentToDelete?.id ?? shipmentToDelete?._id}` : '');

  return (
    <div className="shipments-page-root">
      <CreateBusinessHeader
        title="Планы доставки"
        subtitle=""
        onBack={handleBack}
      />

      <main className="shipments-page-content">
        {loading ? (
          <div className="shipments-page-loading" aria-busy="true">
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
          </div>
        ) : error ? (
          <div className="shipments-page-error">{error}</div>
        ) : (
          <>
            <div className="main-page-sectionHeader shipments-page-sectionHeader">
              <div className="main-page-sectionSub">
                <span>
                  {shipments.length
                    ? `${shipments.length} ${
                        shipments.length === 1
                          ? 'план'
                          : shipments.length >= 2 && shipments.length <= 4
                            ? 'плана'
                            : 'планов'
                      }`
                    : 'Нет планов доставки'}
                </span>
              </div>
            </div>

            {shipments.length === 0 ? (
              <div className="shipments-page-empty">Нет планов доставки</div>
            ) : (
              <ul className="shipments-page-list" aria-label="Список доставок">
                {shipments.map((shipment) => {
                  const id = shipment?.id ?? shipment?._id;
                  const locationName = shipment?.locationName ?? `Доставка #${id}`;
                  const priceLabel = formatPrice(shipment?.price);

                  return (
                    <li key={id} className="shipments-page-item">
                      <div className="shipments-page-itemRow">
                        <button
                          type="button"
                          className="shipments-page-itemMain"
                          onClick={() => handleEdit(shipment)}
                          aria-label={`Редактировать «${locationName}»`}
                        >
                          <span className="shipments-page-itemName">{locationName}</span>
                          <span className="shipments-page-itemPrice">{priceLabel} сом</span>
                        </button>
                        <div className="shipments-page-itemActions main-page-productActions">
                          <IconButton
                            onClick={() => setShipmentToDelete(shipment)}
                            label={`Удалить «${locationName}»`}
                            title="Удалить"
                            className="main-page-iconButton"
                          >
                            <TrashIcon />
                          </IconButton>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>

      <footer className="shipments-page-footerRow">
        <button
          type="button"
          className="shipments-page-footerButton"
          onClick={handleCreate}
          disabled={!branchId || branchId === 'store'}
        >
          + Добавить доставку
        </button>
      </footer>

      <DeleteProductModal
        isOpen={!!shipmentToDelete}
        onClose={() => setShipmentToDelete(null)}
        title="Удалить доставку"
        productName={deleteName}
        deleteError={deleteError}
        loading={httpLoading}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default ShipmentsPage;
